import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FaRegCalendarCheck } from 'react-icons/fa';

import { apiService } from '../../components/Config/api.service';
import LoadingBarWithStyles from '../../components/Utils/LoadingBarWithStyles';
import { signalerErreur } from '../../components/Utils/signalerErreur';
import PiecesJointes from './PiecesJointes';

/**
 * Suivi de ses propres demandes de congé (contrat C10, Yros/Kavero#327).
 *
 * Aucun contrat nouveau : `res.users.yros_rh_lister_demandes()` filtre déjà
 * par employé quand le compte est Portail (`if self.share:`), et
 * `api/rh/demandes` est déjà dans l'allowlist du proxy. Cet écran est un
 * second appelant de ce qui existe, pas une capacité de plus.
 *
 * La colonne « employé » de la file de validation n'a pas d'objet ici : sur
 * cet écran, c'est toujours lui.
 *
 * `rafraichir` est un compteur poussé par le parent après un dépôt réussi :
 * sans lui, l'employé envoie sa demande et l'écran ne bouge pas.
 */

// L'enveloppe (yros.rh.demande.conge.enveloppe()) traduit déjà l'état natif
// Odoo en vocabulaire de contrat — 'en_attente' / 'validee' / 'refusee' —
// jamais les codes 'draft'/'confirm'/'validate'/'refuse' d'origine. Un état
// inconnu retombe sur son code brut plutôt que sur une case vide.
const COULEUR_ETAT = {
  validee: 'bg-green-100 text-green-800',
  refusee: 'bg-red-100 text-red-800',
};
const COULEUR_ETAT_DEFAUT = 'bg-yellow-100 text-yellow-800';

/**
 * Période en clair : « 17–21 août 2026 » plutôt que deux dates ISO.
 *
 * `formatRange` écrit l'intervalle selon la langue et ne répète que ce qui
 * change — « 17–21 août 2026 » en français, « August 17 – 21, 2026 » en
 * anglais. Le découper à la main donnerait un ordre de mots faux dès qu'on
 * sort du français, et cette table se lit d'abord à 375px.
 *
 * Retourne null sur une date inexploitable ou un moteur sans `formatRange` :
 * l'appelant réaffiche alors les valeurs brutes plutôt que « Invalid Date ».
 */
function formaterPeriode(debut, fin, locale) {
  const dateDebut = new Date(debut);
  const dateFin = new Date(fin);
  if (Number.isNaN(dateDebut.getTime()) || Number.isNaN(dateFin.getTime())) {
    return null;
  }

  const format = new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
  if (typeof format.formatRange !== 'function') {
    return null;
  }
  return format.formatRange(dateDebut, dateFin);
}

function MesDemandes({ rafraichir = 0 }) {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.resolvedLanguage?.startsWith('en') ? 'en-US' : 'fr-FR';
  const [demandes, setDemandes] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState(null);

  const charger = useCallback(async () => {
    setChargement(true);
    setErreur(null);
    try {
      const reponse = await apiService.listerDemandesRh();
      setDemandes(reponse?.demandes || []);
    } catch (e) {
      signalerErreur(e, 'MesDemandes.charger');
      setDemandes([]);
      setErreur(t('mesDemandes.erreurGenerique'));
    } finally {
      setChargement(false);
    }
  }, [t]);

  useEffect(() => {
    charger();
  }, [charger, rafraichir]);

  return (
    <div className="bg-white rounded-2xl p-4 sm:p-5 shadow-sm border border-slate-100">
      <div className="flex items-center gap-2 mb-3.5 pb-2.5 border-b border-slate-100">
        <span className="w-8 h-8 rounded-xl bg-teal-500/10 text-teal-600 flex items-center justify-center flex-shrink-0">
          <FaRegCalendarCheck className="w-4 h-4" />
        </span>
        <h3 className="text-sm sm:text-base font-bold text-slate-800 tracking-tight">
          {t('mesDemandes.titre')}
        </h3>
      </div>

      {erreur && (
        <p
          data-testid="mes-demandes-erreur"
          className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
        >
          {erreur}
        </p>
      )}

      {chargement ? (
        <LoadingBarWithStyles />
      ) : !erreur && demandes.length === 0 ? (
        <p
          data-testid="mes-demandes-vide"
          className="py-8 text-center text-sm text-gray-500"
        >
          {t('mesDemandes.vide')}
        </p>
      ) : (
        !erreur && (
          <div className="overflow-x-auto">
            <table className="table w-full">
              <thead>
                <tr>
                  <th>{t('mesDemandes.colType')}</th>
                  <th>{t('mesDemandes.colPeriode')}</th>
                  <th>{t('mesDemandes.colEtat')}</th>
                </tr>
              </thead>
              <tbody>
                {demandes.map((demande) => {
                  const libelle = demande.contenu?.libelle || demande.type_conge || demande.type;
                  const dateDebut = demande.contenu?.date_debut || demande.date_debut;
                  const dateFin = demande.contenu?.date_fin || demande.date_fin;
                  const periode =
                    formaterPeriode(dateDebut, dateFin, dateLocale) ||
                    t('mesDemandes.periode', {
                      debut: dateDebut,
                      fin: dateFin,
                    });

                  return (
                    <tr key={demande.id} data-testid={`mes-demandes-ligne-${demande.id}`}>
                      <td className="font-semibold">{libelle}</td>
                      <td className="whitespace-nowrap">{periode}</td>
                      <td>
                        <span
                          className={`text-xs font-medium px-2.5 py-1 rounded-full ${
                            COULEUR_ETAT[demande.etat] || COULEUR_ETAT_DEFAUT
                          }`}
                        >
                          {t(`mesDemandes.etats.${demande.etat}`, {
                            defaultValue: demande.etat,
                          })}
                        </span>
                        {/* Motif du refus (Yros/Kavero#418), comme sur « Mes avances ». */}
                        {demande.motif_refus && (
                          <p
                            data-testid={`mes-demandes-motif-${demande.id}`}
                            className="mt-1 text-xs text-red-800"
                          >
                            <span className="font-medium">{t('mesDemandes.motifRefus')} : </span>
                            {demande.motif_refus}
                          </p>
                        )}
                        {/* Pièces déposées avec la demande (Yros/Kavero#431). */}
                        <PiecesJointes demande={demande} className="mt-1" />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )
      )}
    </div>
  );
}

export default MesDemandes;
